/**
 * Date utility functions
 * Pure functions for date formatting and conversion
 */

import { capitalizeFirst } from './string.js';

// ── App-wide display format ────────────────────────────────────────────────────
// THE single source of truth for how dates look and are typed (dd/mm/yyyy). Storage is
// always ISO yyyy-mm-dd. Everything that shows, masks, parses or configures a date
// picker goes through these exports; `npm run verify:dates` fails on hardcoded formats
// anywhere else. To change the format, change it here.
export const DATE_SEPARATOR = '/';
/** flatpickr `dateFormat` for the display format ('d/m/Y'). */
export const FLATPICKR_DATE_FORMAT = ['d', 'm', 'Y'].join(DATE_SEPARATOR);
const DISPLAY_DATE_RE = /^(\d{2})[\/\-](\d{2})[\/\-](\d{4})$/;
const pad2 = (n) => String(n).padStart(2, '0');

/** Input placeholder for the display format: 'dd/mm/yyyy' (en) or 'dd/mm/aaaa' (es). */
export function datePlaceholder(lang = 'en') {
    return ['dd', 'mm', lang === 'es' ? 'aaaa' : 'yyyy'].join(DATE_SEPARATOR);
}

/**
 * Display a date as dd/mm/yyyy.
 * @param {string|Date} value - ISO date, ISO timestamp, dd/mm/yyyy (either separator) or Date
 * @returns {string} dd/mm/yyyy, or '' when there is no valid date
 */
export function formatDisplayDate(value) {
    if (!value) return '';
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? '' : [pad2(value.getDate()), pad2(value.getMonth() + 1), value.getFullYear()].join(DATE_SEPARATOR);
    }
    const s = String(value).trim();
    const iso = looksLikeISO(s) ? s : (s.length > 10 && s[10] === 'T' && looksLikeISO(s.slice(0, 10)) ? s.slice(0, 10) : (looksLikeDMY(s) ? toISOFromDMY(s) : ''));
    if (!iso) return '';
    const [y, m, d] = iso.split('-');
    return [d, m, y].join(DATE_SEPARATOR);
}

/**
 * Display a moment as 'dd/mm/yyyy, HH:MM' (24h), optionally in a time zone.
 * @param {string|number|Date} value
 * @param {{ timeZone?: string }} [options]
 */
export function formatDisplayDateTime(value, { timeZone } = {}) {
    const date = value instanceof Date ? value : new Date(value);
    if (!value || Number.isNaN(date.getTime())) return '';
    if (timeZone) {
        try {
            const parts = new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(date);
            const read = (type) => parts.find((part) => part.type === type)?.value || '';
            return `${[read('day'), read('month'), read('year')].join(DATE_SEPARATOR)}, ${read('hour')}:${read('minute')}`;
        } catch { /* invalid zone: fall back to local time */ }
    }
    return `${formatDisplayDate(date)}, ${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

/**
 * Parse what a person typed (dd/mm/yyyy; '-' accepted too) into ISO yyyy-mm-dd.
 * @returns {string} ISO date, or '' when incomplete or not a real calendar date
 */
export function parseDisplayDate(text) {
    const match = DISPLAY_DATE_RE.exec(String(text || '').trim());
    if (!match) return '';
    const [, dd, mm, yyyy] = match;
    const date = new Date(Date.UTC(+yyyy, +mm - 1, +dd));
    if (date.getUTCFullYear() !== +yyyy || date.getUTCMonth() !== +mm - 1 || date.getUTCDate() !== +dd) return '';
    return `${yyyy}-${mm}-${dd}`;
}

/** True when `text` is a complete dd/mm/yyyy (either separator), valid or not. */
export function isCompleteDisplayDate(text) {
    return DISPLAY_DATE_RE.test(String(text || '').trim());
}

/** Typing mask: keeps digits, inserts separators, clamps day ≤ 31 and month ≤ 12. */
export function maskDisplayDate(text) {
    const digits = String(text || '').replace(/\D/g, '').slice(0, 8);
    let day = digits.slice(0, 2);
    if (day.length === 2 && parseInt(day, 10) > 31) day = '31';
    let month = digits.slice(2, 4);
    if (month.length === 2 && parseInt(month, 10) > 12) month = '12';
    return [day, month, digits.slice(4, 8)].filter((part, index) => index === 0 || digits.length > index * 2).join(DATE_SEPARATOR);
}

/** Characters a date field accepts while typing (digits and either separator). */
export function isDateInputChar(char) {
    return /[\d\/\-]/.test(char);
}

/** Local Date → ISO yyyy-mm-dd ('' for an invalid date). */
export function isoFromDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Calendar date in the user's zone; omitted zone means the browser's local zone. */
export function isoDateInTimeZone(date, timeZone) {
    if (!timeZone) return isoFromDate(date);
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone, year: 'numeric', month: '2-digit', day: '2-digit'
    }).formatToParts(date);
    const value = type => parts.find(part => part.type === type).value;
    return `${value('year')}-${value('month')}-${value('day')}`;
}

/**
 * Check if string looks like DD/MM/YYYY or DD-MM-YYYY format
 * @param {string} s - Date string to check
 * @returns {boolean} True if matches DD/MM/YYYY pattern
 *
 * @example
 * looksLikeDMY('25/12/2025') // Returns: true
 * looksLikeDMY('2025-12-25') // Returns: false
 */
export function looksLikeDMY(s) {
    return (
        typeof s === "string" &&
        /^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{4}$/.test(s.trim())
    );
}

/**
 * Check if string looks like ISO YYYY-MM-DD format
 * @param {string} s - Date string to check
 * @returns {boolean} True if matches ISO date pattern
 *
 * @example
 * looksLikeISO('2025-12-25') // Returns: true
 * looksLikeISO('25/12/2025') // Returns: false
 */
export function looksLikeISO(s) {
    return typeof s === "string" && /^\d{4}-\d{2}-\d{2}$/.test(s.trim());
}

/**
 * Convert DD/MM/YYYY or DD-MM-YYYY to ISO YYYY-MM-DD
 * @param {string} s - Date string in DD/MM/YYYY format
 * @returns {string} ISO formatted date or original string if invalid
 *
 * @example
 * toISOFromDMY('25/12/2025') // Returns: '2025-12-25'
 * toISOFromDMY('invalid') // Returns: 'invalid'
 */
export function toISOFromDMY(s) {
    if (!looksLikeDMY(s)) return s || "";
    const parts = s.trim().split(/[\/\-]/);
    const d = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const y = parseInt(parts[2], 10);
    if (!d || !m || !y || d > 31 || m > 12) return "";
    const dd = String(d).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    return `${y}-${mm}-${dd}`;
}

/**
 * Convert ISO YYYY-MM-DD to the display format (dd/mm/yyyy)
 * @param {string} s - ISO date string
 * @returns {string} Display date, or the original string if it is not ISO
 *
 * @example
 * toDMYFromISO('2025-12-25') // Returns: '25/12/2025'
 * toDMYFromISO('invalid') // Returns: 'invalid'
 */
export function toDMYFromISO(s) {
    if (!looksLikeISO(s)) return s || "";
    return formatDisplayDate(s);
}

/**
 * Format a date for display (dd/mm/yyyy)
 * @param {string} s - ISO date, ISO timestamp or DD/MM/YYYY (either separator)
 * @returns {string} Display date, or 'No date'
 *
 * @example
 * formatDate('2025-12-25') // Returns: '25/12/2025'
 * formatDate('25-12-2025') // Returns: '25/12/2025'
 * formatDate(null) // Returns: 'No date'
 */
export function formatDate(s) {
    return formatDisplayDate(s) || "No date";
}

/**
 * Format date in human-friendly format (e.g. "Oct 10, 2025")
 * @param {string} s - Date string (ISO or DD/MM/YYYY)
 * @returns {string} Formatted date like "Dec 25, 2025"
 *
 * @example
 * formatDatePretty('2025-12-25') // Returns: 'Dec 25, 2025'
 * formatDatePretty(null) // Returns: 'No date'
 */
export function formatDatePretty(s, locale = undefined) {
    if (!s) return "No date";
    try {
        // ISO yyyy-mm-dd
        if (looksLikeISO(s)) {
            const [y, m, d] = s.split("-");
            const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
            return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
        }

        // dd/mm/yyyy or dd-mm-yyyy
        if (looksLikeDMY(s)) {
            const parts = s.split(/[\/\-]/);
            const d = parseInt(parts[0], 10);
            const m = parseInt(parts[1], 10);
            const y = parseInt(parts[2], 10);
            const date = new Date(y, m - 1, d);
            return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric' });
        }
    } catch (e) {
        // fallthrough
    }
    return "No date";
}

/**
 * Format activity timestamp in relative time (Today, Yesterday, 3d ago, etc.)
 * @param {string} dateString - ISO timestamp
 * @returns {string} Relative time description
 *
 * @example
 * formatActivityDate(new Date().toISOString()) // Returns: 'Today'
 * formatActivityDate('2025-12-20') // Returns: 'Dec 20' (if not recent)
 */
export function formatActivityDate(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Get localized day names for calendar display
 * @param {string} locale - Locale string (e.g., 'en-US', 'es-ES')
 * @returns {string[]} Array of 7 day names starting from Monday
 *
 * @example
 * getCalendarDayNames('en-US') // Returns: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
 */
export function getCalendarDayNames(locale) {
    const formatter = new Intl.DateTimeFormat(locale, { weekday: 'short' });
    const baseDate = new Date(2024, 0, 1); // Monday
    return Array.from({ length: 7 }, (_, idx) => {
        const label = formatter.format(new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate() + idx));
        return capitalizeFirst(label);
    });
}

/**
 * Format month and year for calendar header
 * @param {string} locale - Locale string (e.g., 'en-US', 'es-ES')
 * @param {number} year - Year number
 * @param {number} month - Month number (0-11)
 * @returns {string} Formatted month and year (e.g., 'January 2025')
 *
 * @example
 * formatCalendarMonthYear('en-US', 2025, 0) // Returns: 'January 2025'
 */
export function formatCalendarMonthYear(locale, year, month) {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' });
    return capitalizeFirst(formatter.format(new Date(year, month, 1)));
}

/**
 * Abbreviated month + year, for narrow screens where the full month name
 * would not fit (e.g. 'Sept 2026' / 'Sep 2026' instead of 'Septiembre de 2026').
 * @param {string} locale - BCP-47 locale (e.g. 'en-US', 'es-ES')
 * @param {number} year - Year number
 * @param {number} month - Month number (0-11)
 * @returns {string} Abbreviated month and year
 */
export function formatCalendarMonthYearShort(locale, year, month) {
    const formatter = new Intl.DateTimeFormat(locale, { month: 'short', year: 'numeric' });
    // Drop any trailing period some locales add to the short month (e.g. 'sept.').
    return capitalizeFirst(formatter.format(new Date(year, month, 1)).replace('.', ''));
}

/**
 * Strip time component from a Date, returning only the date at midnight
 * @param {Date} d - Date object to strip time from
 * @returns {Date} New Date object with time set to 00:00:00
 *
 * @example
 * stripTime(new Date('2025-12-25T14:30:00')) // Returns: Date for 2025-12-25T00:00:00
 */
export function stripTime(d) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Docs "updated" stamp: 'dd/mm/yyyy HH:MM' (local time). */
export function formatDocTimestamp(isoStr) {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    if (isNaN(d)) return '';
    return `${formatDisplayDate(d)} ${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function normalizeISODate(dateStr) {
    if (!dateStr) return '';
    if (looksLikeISO(dateStr)) return dateStr;
    if (looksLikeDMY(dateStr)) return toISOFromDMY(dateStr);
    return '';
}
